import type { ComponentChildren, JSX } from "preact";

export const COLOR_PALETTE = [
  "#1a1a2e",
  "#141b33",
  "#0c1e2a",
  "#24203a",
  "#3a2415",
  "#173a2e",
  "#27203a",
  "#3a1a14",
  "#2a2a3a",
  "#3d0f14",
  "#123a2a",
  "#4a000a",
  "#e94560",
  "#f2a33c",
  "#3ddc97",
  "#63d2ff",
  "#5a5a8a",
  "#0a0a12",
];

const EMOJI_PALETTE = [
  "🧑‍💻",
  "👩‍💻",
  "👨‍💻",
  "🤖",
  "👾",
  "🐛",
  "🦀",
  "🐍",
  "📖",
  "⚔️",
  "⚙️",
  "🔥",
  "💾",
  "🧩",
  "🚀",
  "🌌",
];

export function ColorField({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <div className="color-field">
      {COLOR_PALETTE.map((color) => (
        <button
          type="button"
          key={color}
          className={`swatch${value === color ? " selected" : ""}`}
          style={{ background: color }}
          aria-label={color}
          onClick={() => onChange(color)}
        />
      ))}
    </div>
  );
}

export function EmojiField({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  return (
    <div className="color-field">
      {EMOJI_PALETTE.map((emoji) => (
        <button
          type="button"
          key={emoji}
          className={`emoji-btn${value === emoji ? " selected" : ""}`}
          onClick={() => onChange(emoji)}
        >
          {emoji}
        </button>
      ))}
      <input
        className="mini-input"
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        aria-label="custom emoji"
      />
    </div>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ComponentChildren;
}): JSX.Element {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {hint ? <span className="field-hint">{hint}</span> : null}
      {children}
    </label>
  );
}

export function TextInput(props: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  onConfirm?: () => void;
}): JSX.Element {
  const { onChange, onConfirm, ...rest } = props;
  return (
    <input
      {...rest}
      className="editor-input"
      value={props.value}
      onInput={(e) => onChange(e.currentTarget.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && onConfirm) onConfirm();
      }}
    />
  );
}

export function TextArea(props: {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string;
}): JSX.Element {
  const { onChange, ...rest } = props;
  return (
    <textarea
      {...rest}
      className="editor-input editor-textarea"
      value={props.value}
      onInput={(e) => onChange(e.currentTarget.value)}
    />
  );
}

export function ButtonGroup({
  children,
}: {
  children: ComponentChildren;
}): JSX.Element {
  return <div className="button-row">{children}</div>;
}

export function ToastBanner({
  kind,
  message,
}: {
  kind: "error" | "ok" | "warn";
  message: string | null;
}): JSX.Element | null {
  if (!message) return null;
  return (
    <div className={`toast-banner ${kind}`}>
      <span>
        {kind === "error" ? "⚠ " : kind === "ok" ? "✓ " : "!"}
        {message}
      </span>
    </div>
  );
}

export type VarValue = string | number | boolean;

export function detype(raw: string): VarValue {
  const s = raw.trim();
  if (s === "true") return true;
  if (s === "false") return false;
  if (s === "" || s === "-") return s;
  if (!Number.isNaN(Number(s))) return Number(s);
  return s;
}

const VAR_STYLES = ["number", "string", "boolean"] as const;
type VarStyle = (typeof VAR_STYLES)[number];

function styleFor(value: VarValue): VarStyle {
  if (typeof value === "boolean") return "boolean";
  if (typeof value === "number") return "number";
  return "string";
}

export function VarMapEditor({
  value,
  onChange,
  placeholder,
}: {
  value: Record<string, VarValue>;
  onChange: (v: Record<string, VarValue>) => void;
  placeholder?: string;
}): JSX.Element {
  const rows = Object.entries(value);
  const update = (next: Record<string, VarValue>): void => onChange(next);

  return (
    <div className="var-map">
      {rows.map(([name, val]) => {
        const style = styleFor(val);
        return (
          <div className="row-pair" key={name}>
            <input
              className="editor-input"
              value={name}
              placeholder="variable name"
              onInput={(e) => {
                const next = { ...value };
                const old = val;
                delete next[name];
                next[e.currentTarget.value] = old;
                update(next);
              }}
            />
            <select
              className="editor-input var-style"
              value={style}
              onChange={(e) => {
                const next = { ...value };
                const raw = String(val);
                const nextStyle = e.currentTarget.value as VarStyle;
                if (nextStyle === "boolean") {
                  next[name] = raw === "true" || raw === "1";
                } else if (nextStyle === "number") {
                  next[name] = Number(raw);
                } else {
                  next[name] = String(val);
                }
                update(next);
              }}
            >
              {VAR_STYLES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <input
              className="editor-input"
              value={String(val)}
              placeholder={placeholder ?? "value"}
              onInput={(e) => {
                const next = { ...value };
                const raw = e.currentTarget.value;
                next[name] =
                  style === "boolean"
                    ? raw === "true" || raw === "1"
                    : style === "number"
                      ? Number(raw)
                      : raw;
                update(next);
              }}
            />
            <button
              className="icon-btn"
              title="Remove"
              onClick={() => {
                const next = { ...value };
                delete next[name];
                update(next);
              }}
            >
              ×
            </button>
          </div>
        );
      })}
      <div className="row-pair">
        <button
          className="choice-button"
          onClick={() => {
            let name = "var";
            let i = 1;
            const next = { ...value };
            while (name in next) {
              name = `var_${i}`;
              i += 1;
            }
            next[name] = 0;
            update(next);
          }}
        >
          + Add variable
        </button>
      </div>
    </div>
  );
}
